import { Link } from 'react-router-dom';

// SEO content sections for tool pages. Google needs real text on a page to
// rank it — a bare tool with no words ranks poorly. Drop this component at
// the bottom of a tool page with the matching key:
//
//   import ToolSeoContent from 'components/ToolSeoContent';
//   ...
//   <ToolSeoContent tool="image-compress" />
//
// Keys available: image-compress, image-to-pdf, resume-builder,
//                 pdf-compress, merge-pdf, pdf-to-word

const CONTENT = {
  'image-compress': {
    heading: 'Compress Images to an Exact Size — 20KB, 50KB, 100KB',
    intro: [
      `Most Indian exam and government portals set a strict photo size limit — SSC forms often ask for a photo under 50KB and a signature between 10KB and 20KB, while UPSC, IBPS, and railway applications have their own limits. If your file is even slightly over, the upload fails. This free image compressor solves that in seconds: choose a target size like 20KB, 50KB, or 100KB, and the tool automatically adjusts quality to fit your photo under that exact limit.`,
      `Unlike most compressors that only offer a quality slider, this tool has a dedicated Target Size mode built for form uploads. It works with JPG, PNG, and WEBP files, and everything happens in your browser — your photo is never uploaded to any server, so your documents stay completely private. There is no signup, no watermark, and no daily limit.`,
    ],
    faqs: [
      { q: 'How do I compress a photo to 50KB for SSC forms?', a: 'Upload your photo, select the 50 KB preset in Target Size mode, and click compress. The tool automatically finds the best quality that keeps the file under 50KB, then downloads it as a JPG.' },
      { q: 'Will compressing reduce my photo quality?', a: 'The tool keeps the highest possible quality that fits your target size. For typical form photos, the result stays clear and perfectly acceptable for uploads.' },
      { q: 'Is my photo uploaded to a server?', a: 'No. Compression happens entirely in your browser. Your photo never leaves your device.' },
      { q: 'What formats are supported?', a: 'JPG, JPEG, PNG, and WEBP images up to 20MB. The compressed output is saved as JPG.' },
    ],
    related: [
      { name: 'Image Resize', path: '/tools/image/resize' },
      { name: 'Image to PDF', path: '/tools/pdf/image-to-pdf' },
      { name: 'Compress PDF', path: '/tools/pdf/compress' },
    ],
  },

  'image-to-pdf': {
    heading: 'Convert Any Image to PDF — Free, Private, Instant',
    intro: [
      `Many portals in India only accept PDF uploads — exam forms, college assignment submissions, and job applications often reject plain JPG or PNG files. This free converter turns any image into a clean PDF in one click. Upload a photo of a document, mark sheet, ID proof, or handwritten assignment, and download a ready-to-submit PDF in seconds.`,
      `The conversion happens entirely in your browser, so your documents are never uploaded to any server — important when you are handling ID cards, certificates, or personal papers. There is no signup, no watermark, and no limit on how many files you can convert. If the resulting PDF exceeds a portal's size limit, compress it with the free PDF Compressor and it will fit.`,
    ],
    faqs: [
      { q: 'How do I convert a JPG to PDF for free?', a: 'Upload the JPG, click convert, and download the PDF. It takes under thirty seconds and requires no account.' },
      { q: 'Can I combine multiple images into one PDF?', a: 'Convert each image to PDF, then join them with the free Merge PDF tool to build a single multi-page document.' },
      { q: 'Is the converted PDF accepted by government portals?', a: 'Yes — the output is a standard PDF file accepted by SSC, UPSC, banking, and college portals. Just check the portal\u2019s size limit and compress if needed.' },
      { q: 'Does it work on mobile?', a: 'Yes. You can pick a photo from your phone gallery, convert it, and download the PDF directly on your phone.' },
    ],
    related: [
      { name: 'Compress PDF', path: '/tools/pdf/compress' },
      { name: 'Merge PDF', path: '/tools/pdf/merge' },
      { name: 'Image Compressor', path: '/tools/image/compress' },
    ],
  },

  'resume-builder': {
    heading: 'Free Resume Builder with 6 ATS-Friendly Templates',
    intro: [
      `A strong resume decides whether your application gets a callback — and you should not have to pay for one. This free resume builder gives you six professionally designed templates: Classic, Professional, Modern, Minimal, Elegant, and a dedicated ATS template built to pass the applicant tracking software that most large companies use to filter resumes before a human ever reads them.`,
      `Fill in your details and watch the resume build itself in the live preview. Pick an accent colour, switch templates with one click, and download a polished PDF instantly. Only your name, email, phone, and education are required — every other section is optional and simply disappears from the final PDF if you leave it blank. Your details are processed entirely in your browser and are never uploaded or stored anywhere, so your personal information stays private.`,
    ],
    faqs: [
      { q: 'Is this resume builder really free?', a: 'Yes — all six templates, all colours, and unlimited PDF downloads are free. No signup, no watermark, no hidden charges.' },
      { q: 'Which template should freshers use?', a: 'For job portals and large companies, the ATS template is the safest choice because it passes automated screening reliably. For startups and creative roles, Modern or Elegant stand out visually.' },
      { q: 'What is an ATS-friendly resume?', a: 'An ATS (Applicant Tracking System) is software that scans resumes before recruiters see them. An ATS-friendly resume uses a simple single-column layout, standard headings, and no graphics, so the software reads it correctly.' },
      { q: 'Is my personal data safe?', a: 'Yes. Everything runs in your browser — your details are never sent to a server, and nothing is stored after you close the page.' },
    ],
    related: [
      { name: 'Image Compressor (photo for forms)', path: '/tools/image/compress' },
      { name: 'Image to PDF', path: '/tools/pdf/image-to-pdf' },
      { name: 'QR Code Generator', path: '/tools/utility/qr-code' },
    ],
  },

  'pdf-compress': {
    heading: 'Compress PDF Online — Fit Any Upload Size Limit',
    intro: [
      `Upload portals rarely accept large PDFs — many Indian government and exam websites cap documents at 100KB, 200KB, or 500KB, and email attachments have their own limits. This free PDF compressor reduces your file size while keeping the document readable, so your upload goes through the first time.`,
      `It is completely free with no signup and no watermark, and works on both phone and laptop. Compress mark sheets, ID proofs, application forms, and reports in seconds. If you are starting from a photo rather than a PDF, compress the image first with the Image Compressor and then convert it — that combination produces the smallest possible file for strict portals.`,
    ],
    faqs: [
      { q: 'How do I compress a PDF to 100KB?', a: 'Upload your PDF and run the compressor. If the result is still above your limit and your PDF is image-based, compress the source image first with the Image Compressor, then convert it back to PDF.' },
      { q: 'Will my document still be readable?', a: 'Yes — the compressor balances size and quality so text remains clear for reviewers and portals.' },
      { q: 'Is there a file limit?', a: 'No daily limits. Compress as many PDFs as you need, free.' },
    ],
    related: [
      { name: 'Merge PDF', path: '/tools/pdf/merge' },
      { name: 'Split PDF', path: '/tools/pdf/split' },
      { name: 'Image Compressor', path: '/tools/image/compress' },
    ],
  },

  'merge-pdf': {
    heading: 'Merge PDF Files into One Document — Free',
    intro: [
      `When a portal asks for "all documents in a single PDF", this tool does the job in seconds. Combine mark sheets, certificates, ID proofs, or assignment pages into one tidy, ordered PDF — no software installation, no signup, and no watermark on the result.`,
      `Merging is handled securely, and the final file downloads straight to your device. Students use it to submit multi-page assignments, job seekers combine certificates for applications, and offices join scanned pages into a single report. If the merged file ends up over a portal's size limit, run it through the free PDF Compressor afterwards.`,
    ],
    faqs: [
      { q: 'How do I combine multiple PDFs into one?', a: 'Upload your PDF files in the order you want, click merge, and download the single combined document.' },
      { q: 'Can I merge images and PDFs together?', a: 'Convert images to PDF first with the Image to PDF tool, then merge everything into one file.' },
      { q: 'Is it free without a watermark?', a: 'Yes — completely free, unlimited use, and no watermark is added to your document.' },
    ],
    related: [
      { name: 'Split PDF', path: '/tools/pdf/split' },
      { name: 'Compress PDF', path: '/tools/pdf/compress' },
      { name: 'Image to PDF', path: '/tools/pdf/image-to-pdf' },
    ],
  },

  'split-pdf': {
    heading: 'Split PDF Online — Extract the Pages You Need',
    intro: [
      `Sometimes a form asks for just one or two pages from a larger PDF — a single mark sheet from a combined document, or specific pages from a scanned file. This free Split PDF tool lets you pull out exactly the pages you need and save them as a new PDF, in seconds.`,
      `It works entirely free with no signup and no watermark, on both phone and laptop. Split application forms, extract a chapter from study material, or separate pages before uploading to a portal. If you later need to recombine pages in a different order, use the free Merge PDF tool.`,
    ],
    faqs: [
      { q: 'How do I extract specific pages from a PDF?', a: 'Upload your PDF, choose the pages or range you want, and download them as a new PDF file.' },
      { q: 'Is splitting a PDF free?', a: 'Yes — completely free, unlimited use, and no watermark is added.' },
      { q: 'Can I reorder pages after splitting?', a: 'Yes — split out the pages you need, then use Merge PDF to combine them in any order.' },
    ],
    related: [
      { name: 'Merge PDF', path: '/tools/pdf/merge' },
      { name: 'Compress PDF', path: '/tools/pdf/compress' },
      { name: 'PDF to JPG', path: '/tools/pdf/to-jpg' },
    ],
  },

  'pdf-to-jpg': {
    heading: 'Convert PDF to JPG Images — Free Online',
    intro: [
      `Need each page of a PDF as a separate image? This free tool converts your PDF into JPG images — one per page — that you can share on WhatsApp, insert into a document, or upload where only images are accepted. No software and no signup required.`,
      `Conversion is free with no watermark, and works on phone and laptop. Turn presentation slides, notes, or a scanned document into shareable images in seconds. If you need the reverse — images back into a single PDF — use the free Image to PDF tool.`,
    ],
    faqs: [
      { q: 'How do I convert a PDF to JPG?', a: 'Upload your PDF and download the JPG images — one image is created for each page.' },
      { q: 'Will the image quality be good?', a: 'Yes, pages are rendered at high resolution so text and images stay clear.' },
      { q: 'Is it free?', a: 'Completely free, with no watermark and no daily limit.' },
    ],
    related: [
      { name: 'Image to PDF', path: '/tools/pdf/image-to-pdf' },
      { name: 'Split PDF', path: '/tools/pdf/split' },
      { name: 'Image Compressor', path: '/tools/image/compress' },
    ],
  },

  'pdf-to-zip': {
    heading: 'Convert PDF to ZIP — Compress and Package Your File',
    intro: [
      `A ZIP file packages your PDF into a smaller, easy-to-share archive — handy when you need to email several documents together or reduce the size for an upload. This free tool zips your PDF in seconds, with no signup and no watermark.`,
      `It runs free on both phone and laptop. Package application documents, share multiple files as one download, or shrink a PDF for email attachments. If you only need the PDF smaller (not zipped), the free PDF Compressor is the better choice.`,
    ],
    faqs: [
      { q: 'Why convert a PDF to ZIP?', a: 'A ZIP archive is smaller and lets you bundle files together, which is useful for email and uploads.' },
      { q: 'Is it free to use?', a: 'Yes — free, unlimited, and no watermark.' },
      { q: 'Should I zip or compress my PDF?', a: 'Zip it to bundle or archive; use the PDF Compressor if you just need the PDF itself to be smaller.' },
    ],
    related: [
      { name: 'Compress PDF', path: '/tools/pdf/compress' },
      { name: 'Merge PDF', path: '/tools/pdf/merge' },
      { name: 'PDF to Word', path: '/tools/pdf/to-word' },
    ],
  },

  'jpg-to-png': {
    heading: 'Convert JPG to PNG Online — Free & Lossless',
    intro: [
      `PNG is the format to choose when you need a transparent background or the sharpest possible quality — logos, graphics, and screenshots all look best as PNG. This free tool converts your JPG images to PNG in seconds, with no signup and no watermark.`,
      `Everything runs in your browser, so your images stay private. Convert photos for design work, prepare graphics that need a clean background, or get a lossless copy of an image. If you need the opposite — smaller file sizes — convert to JPG instead, or use the Image Compressor.`,
    ],
    faqs: [
      { q: 'Why convert JPG to PNG?', a: 'PNG supports transparency and lossless quality, which is better for logos, graphics, and images you will edit further.' },
      { q: 'Will the quality improve?', a: 'PNG preserves quality without further compression, though it cannot recover detail already lost in the original JPG.' },
      { q: 'Is it free?', a: 'Yes — free, unlimited, and no watermark.' },
    ],
    related: [
      { name: 'PNG to JPG', path: '/tools/image/png-to-jpg' },
      { name: 'Image Compressor', path: '/tools/image/compress' },
      { name: 'Image Resize', path: '/tools/image/resize' },
    ],
  },

  'png-to-jpg': {
    heading: 'Convert PNG to JPG Online — Smaller File Size',
    intro: [
      `JPG files are much smaller than PNG, which makes them ideal for uploads, email, and web pages where file size matters. This free tool converts your PNG images to JPG in seconds — perfect for meeting the size limits on exam forms and portals — with no signup and no watermark.`,
      `Conversion happens in your browser, keeping your images private. Transparent areas are filled with a white background so the JPG looks clean. If you need an even smaller file for a strict size limit, follow up with the Image Compressor.`,
    ],
    faqs: [
      { q: 'Why convert PNG to JPG?', a: 'JPG files are smaller, so they upload faster and fit within portal size limits more easily.' },
      { q: 'What happens to transparent areas?', a: 'JPG does not support transparency, so transparent areas are filled with white for a clean result.' },
      { q: 'Is it free?', a: 'Yes — completely free with no watermark or daily limit.' },
    ],
    related: [
      { name: 'JPG to PNG', path: '/tools/image/jpg-to-png' },
      { name: 'Image Compressor', path: '/tools/image/compress' },
      { name: 'Image to PDF', path: '/tools/pdf/image-to-pdf' },
    ],
  },

  'image-resize': {
    heading: 'Resize Images Online — Exact Dimensions, Free',
    intro: [
      `Need an image at a specific size — a passport photo, a profile picture, or an exact pixel dimension for a form? This free tool resizes any image to the width and height you choose, or to a ready preset, in seconds. No signup, no watermark.`,
      `It runs entirely in your browser, so your photo is never uploaded and stays private. Resize photos for exam and job application forms, social media, or website use. If the file also needs to be under a KB limit after resizing, run it through the Image Compressor.`,
    ],
    faqs: [
      { q: 'How do I resize an image to exact pixels?', a: 'Upload your image, enter the width and height you need (or pick a preset), and download the resized image.' },
      { q: 'Will resizing distort my photo?', a: 'Keep the aspect ratio locked to avoid stretching. The tool lets you resize proportionally for a clean result.' },
      { q: 'Is my image uploaded anywhere?', a: 'No — resizing happens in your browser and your photo never leaves your device.' },
    ],
    related: [
      { name: 'Image Compressor', path: '/tools/image/compress' },
      { name: 'Image to PDF', path: '/tools/pdf/image-to-pdf' },
      { name: 'JPG to PNG', path: '/tools/image/jpg-to-png' },
    ],
  },

  'word-counter': {
    heading: 'Free Online Word Counter — Words, Characters, Sentences',
    intro: [
      `Whether you are writing an essay with a word limit, a social media caption with a character cap, or an assignment that must hit a minimum length, this free word counter gives you an instant, accurate count. It counts words, characters, sentences, and paragraphs in real time as you type or paste text.`,
      `There is no signup and nothing is uploaded — your text stays in your browser. Students use it to meet essay and application word limits, and writers use it to track length while drafting. It works on phone and laptop.`,
    ],
    faqs: [
      { q: 'Does it count characters with and without spaces?', a: 'Yes — you get a live count of words, characters, sentences, and paragraphs as you type.' },
      { q: 'Is my text stored anywhere?', a: 'No. Everything is counted in your browser and nothing is saved or uploaded.' },
      { q: 'Is it free?', a: 'Yes — completely free with no limits.' },
    ],
    related: [
      { name: 'Case Converter', path: '/tools/text/case-converter' },
      { name: 'Resume Builder', path: '/tools/utility/resume-builder' },
      { name: 'QR Code Generator', path: '/tools/utility/qr-code' },
    ],
  },

  'case-converter': {
    heading: 'Text Case Converter — UPPERCASE, lowercase, Title Case',
    intro: [
      `Fix the capitalisation of any text in one click. This free case converter transforms your text to UPPERCASE, lowercase, Title Case, or Sentence case instantly — handy when you have text stuck in the wrong case, or need a heading formatted correctly.`,
      `It works entirely in your browser with nothing uploaded, so your text stays private. Students, writers, and anyone formatting documents or captions will find it a quick time-saver. No signup, no limits.`,
    ],
    faqs: [
      { q: 'What case options are available?', a: 'UPPERCASE, lowercase, Title Case, and Sentence case — just paste your text and pick one.' },
      { q: 'Is my text saved?', a: 'No — conversion happens in your browser and nothing is stored or uploaded.' },
      { q: 'Is it free?', a: 'Yes — free and unlimited.' },
    ],
    related: [
      { name: 'Word Counter', path: '/tools/text/word-counter' },
      { name: 'Resume Builder', path: '/tools/utility/resume-builder' },
      { name: 'QR Code Generator', path: '/tools/utility/qr-code' },
    ],
  },

  'qr-code': {
    heading: 'Free QR Code Generator — Create QR Codes Instantly',
    intro: [
      `Turn any link, text, or contact detail into a scannable QR code in seconds. This free QR code generator is perfect for sharing a website, a menu, a WhatsApp number, or an Instagram profile — just enter your link and download a high-quality QR code ready to print or post. No signup, no watermark.`,
      `Everything runs in your browser, so your data stays private. Small businesses use it for menus and payment links, and creators use it to share profiles. Download your QR code and use it on posters, business cards, or online.`,
    ],
    faqs: [
      { q: 'How do I make a QR code for free?', a: 'Enter your link or text, and the QR code is generated instantly. Download it and use it anywhere.' },
      { q: 'Do the QR codes expire?', a: 'No — the QR codes are static and never expire. They point to your link permanently.' },
      { q: 'Can I use it for business?', a: 'Yes — the QR codes are free to use on menus, posters, business cards, and payment links.' },
    ],
    related: [
      { name: 'Resume Builder', path: '/tools/utility/resume-builder' },
      { name: 'Word Counter', path: '/tools/text/word-counter' },
      { name: 'Image Compressor', path: '/tools/image/compress' },
    ],
  },

  'pdf-to-word': {
    heading: 'Convert PDF to Editable Word — Free, In Your Browser',
    intro: [
      `Need to edit a PDF? This converter extracts the text from your PDF and builds a clean, editable Word (.docx) file — with headings and paragraphs preserved — that opens in Microsoft Word and Google Docs. It is built for real documents: notes, chapters, letters, reports, and study material.`,
      `Unlike most converters, this one runs entirely in your browser. Your PDF is never uploaded to any server, conversion starts instantly, and it comfortably handles large files — 25, 50, even 100+ pages. There is no signup, no watermark, and no daily limit. One honest note: scanned PDFs (photos of pages, where you cannot select the text) cannot be converted, because they contain images rather than actual text.`,
    ],
    faqs: [
      { q: 'Is the converted Word file editable?', a: 'Yes — you get a real .docx file with selectable, editable text that opens in Word and Google Docs.' },
      { q: 'Why does my scanned PDF not convert?', a: 'Scanned PDFs are photographs of pages with no actual text inside. Text extraction needs a text-based PDF — if you can select text in your PDF viewer, it will convert.' },
      { q: 'Is there a page limit?', a: 'No practical limit — because conversion runs on your own device, even very large PDFs convert quickly.' },
      { q: 'Is my document private?', a: 'Completely. The file never leaves your browser, so sensitive documents stay on your device.' },
    ],
    related: [
      { name: 'Image to PDF', path: '/tools/pdf/image-to-pdf' },
      { name: 'Compress PDF', path: '/tools/pdf/compress' },
      { name: 'Merge PDF', path: '/tools/pdf/merge' },
    ],
  },
};

export default function ToolSeoContent({ tool }) {
  const c = CONTENT[tool];
  if (!c) return null;

  return (
    <div style={{ marginTop: 40 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--blue-dd)', letterSpacing: '-.3px', marginBottom: 14 }}>
        {c.heading}
      </h2>

      {c.intro.map((p, i) => (
        <p key={i} style={{ fontSize: 15, lineHeight: 1.75, color: 'var(--slate-600)', marginBottom: 14 }}>
          {p}
        </p>
      ))}

      <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--slate-800)', margin: '26px 0 12px' }}>
        Frequently Asked Questions
      </h3>
      {c.faqs.map((f, i) => (
        <div key={i} style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--slate-800)', marginBottom: 4 }}>{f.q}</p>
          <p style={{ fontSize: 14.5, lineHeight: 1.7, color: 'var(--slate-600)' }}>{f.a}</p>
        </div>
      ))}

      <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--slate-800)', margin: '24px 0 10px' }}>
        Related tools
      </h3>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {c.related.map(r => (
          <Link key={r.path} to={r.path} style={{
            fontSize: 13.5, fontWeight: 600, color: 'var(--blue)',
            border: '1.5px solid var(--slate-200)', borderRadius: 999,
            padding: '7px 16px', background: 'var(--white)',
          }}>
            {r.name}
          </Link>
        ))}
      </div>
    </div>
  );
}